import { Box, Grid, InputAdornment } from "@mui/material";
import { FormikProps } from "formik";
import { forwardRef } from "react";
import CurrencyInput from "@/components/CurrencyInput";
import CustomSelect from "@/components/CustomSelect";
import CustomTextArea from "@/components/CustomTextArea";
import FormSection from "@/components/FormSection";
import Input from "@/components/Input";
import InfoTooltipIcon from "@/components/InfoTooltipIcon";
import {
  GROUP_AMOUNT,
  GROUP_DESCRIPTION_ROWS,
  GROUP_HELPER_TEXT,
  GROUP_LABEL,
  GROUP_NAME_MAX_CHARS,
  GROUP_PLACEHOLDER,
  GROUP_SECTION,
  GROUP_TYPE_OPTIONS,
  THERAPY_CATEGORY_OPTIONS,
} from "../group-visit-constant";
import { GroupFormValues } from "../group-visit-types";

export type GroupInformationSectionProps = {
  formik: FormikProps<GroupFormValues>;
  disabled?: boolean;
  /** Amount is collected while editing, not while creating the group. */
  showAmount?: boolean;
  /** Drops the titled card so a single-section form reads as a plain form. */
  hideSectionCard?: boolean;
};

/**
 * @author Rajkumar Rathod
 */
const GroupInformationSection = forwardRef<
  HTMLDivElement,
  GroupInformationSectionProps
>(({ formik, disabled, showAmount = true, hideSectionCard = false }, ref) => {
  const { values, errors, touched, setFieldValue, handleChange, handleBlur } =
    formik;

  const fields = (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Input
          label={GROUP_LABEL.groupName}
          required
          error={touched.groupName && errors.groupName}
          textFieldProps={{
            name: "groupName",
            value: values.groupName,
            placeholder: GROUP_PLACEHOLDER.groupName,
            onChange: handleChange,
            onBlur: handleBlur,
            disabled,
            inputProps: { maxLength: GROUP_NAME_MAX_CHARS },
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <InfoTooltipIcon
                    title={GROUP_LABEL.groupName}
                    content={GROUP_HELPER_TEXT.groupName}
                    ariaLabel={`About ${GROUP_LABEL.groupName}`}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <CustomSelect
          label={GROUP_LABEL.groupType}
          required
          name="groupType"
          options={GROUP_TYPE_OPTIONS}
          value={values.groupType}
          disabled={disabled}
          handleBlur={handleBlur}
          onChange={(value) => setFieldValue("groupType", value)}
          error={touched.groupType && errors.groupType}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <CustomSelect
          label={GROUP_LABEL.therapyCategory}
          required
          name="therapyCategory"
          options={THERAPY_CATEGORY_OPTIONS}
          value={values.therapyCategory}
          disabled={disabled}
          handleBlur={handleBlur}
          onChange={(value) => setFieldValue("therapyCategory", value)}
          error={touched.therapyCategory && errors.therapyCategory}
        />
      </Grid>

      {showAmount && (
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <CurrencyInput
            label={GROUP_LABEL.amountForPatient}
            name="amountForPatient"
            value={values.amountForPatient}
            disabled={disabled}
            min={GROUP_AMOUNT.min}
            max={GROUP_AMOUNT.max}
            currencySymbol={GROUP_AMOUNT.currencySymbol}
            onBlur={handleBlur}
            onChange={(value) => setFieldValue("amountForPatient", value)}
            error={touched.amountForPatient && errors.amountForPatient}
          />
        </Grid>
      )}

      <Grid size={12}>
        <CustomTextArea
          label={GROUP_LABEL.description}
          name="description"
          value={values.description}
          disabled={disabled}
          minRows={GROUP_DESCRIPTION_ROWS}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!(touched.description && errors.description)}
          helperText={touched.description && errors.description}
        />
      </Grid>
    </Grid>
  );

  if (hideSectionCard) {
    return (
      <Box ref={ref} id={GROUP_SECTION.INFORMATION}>
        {fields}
      </Box>
    );
  }

  return (
    <FormSection
      variant="plain"
      ref={ref}
      id={GROUP_SECTION.INFORMATION}
      title={GROUP_LABEL.informationSection}
    >
      {fields}
    </FormSection>
  );
});

GroupInformationSection.displayName = "GroupInformationSection";

export default GroupInformationSection;
